import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import axios from '../teacherLms/adapters/axios';
import { studentApiBase, STUDENT_SESSION_EXPIRED_EVENT } from '../teacherLms/adapters/apiUrl';
import fnc from '../mymod/commFunction';
import { clearStoredAppSession } from '../mymod/appSession';
import { normalizeChatUserId } from '../mymod/normalizeAcaCode';
import { setUserInfo, setUserInfo2, setIsLoggedIn, setIsAdmin, setAcaName, setChoJoongGo, setIsPaid } from '../reduxToolkit/redux';
import { setIsAdmin as setBoardAdmin } from '../reduxToolkit/reduxBoard';

const readToken = () => {
  try { return String(fnc.getCookie('appSessionToken') || ''); } catch { return ''; }
};
const isPersistent = () => {
  try { return sessionStorage.getItem('appSessionToken') == null && localStorage.getItem('appSessionToken') != null; } catch { return false; }
};

export default function useStudentBankSession() {
  const dispatch = useDispatch();
  const [token, setToken] = useState(readToken);
  const tokenRef = useRef(token);
  const requestRef = useRef(null);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading', profile: null });

  const syncSession = useCallback(() => {
    const nextToken = readToken();
    if (nextToken === tokenRef.current) return;
    tokenRef.current = nextToken;
    requestRef.current?.abort();
    setToken(nextToken);
    setState({ status: nextToken ? 'loading' : 'unauthorized', profile: null });
  }, []);

  const invalidateSession = useCallback(expiredToken => {
    if (!expiredToken || expiredToken !== readToken() || expiredToken !== tokenRef.current) {
      syncSession();
      return;
    }
    requestRef.current?.abort();
    clearStoredAppSession();
    dispatch(setUserInfo({}));
    dispatch(setUserInfo2({}));
    dispatch(setIsLoggedIn(false));
    dispatch(setIsAdmin(false));
    dispatch(setBoardAdmin(false));
    syncSession();
    setState({ status: 'unauthorized', profile: null });
  }, [dispatch, syncSession]);

  const retry = useCallback(() => { syncSession(); setAttempt(value => value + 1); }, [syncSession]);

  useEffect(() => {
    const storage = event => { if (!event.key || ['appSessionToken', 'appSessionExpiresAt'].includes(event.key)) syncSession(); };
    const visible = () => { if (document.visibilityState !== 'hidden') syncSession(); };
    const expired = event => invalidateSession(event.detail?.token);
    const events = ['billing:updated', 'pageshow', 'focus'];
    events.forEach(event => window.addEventListener(event, syncSession));
    window.addEventListener('storage', storage);
    window.addEventListener(STUDENT_SESSION_EXPIRED_EVENT, expired);
    document.addEventListener('visibilitychange', visible);
    syncSession();
    return () => {
      events.forEach(event => window.removeEventListener(event, syncSession));
      window.removeEventListener('storage', storage);
      window.removeEventListener(STUDENT_SESSION_EXPIRED_EVENT, expired);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [syncSession, invalidateSession]);

  useEffect(() => {
    if (!token) { setState({ status: 'unauthorized', profile: null }); return undefined; }
    const controller = new AbortController();
    requestRef.current = controller;
    let active = true;
    setState({ status: 'loading', profile: null });
    axios.get(`${studentApiBase}/session`, { timeout: 15000, signal: controller.signal }).then(({ data }) => {
      if (!active || controller.signal.aborted) return;
      if (readToken() !== token || tokenRef.current !== token) { syncSession(); return; }
      if (!data?.success || String(data.user?.kind) !== '1' || data.user?.role !== 'student'
        || !data.user?.id || !data.user?.stCode || !data.academy?.acaDbName || !data.academy?.acaCode
        || String(data.user.acaCode) !== String(data.academy.acaCode)) {
        setState({ status: 'forbidden', profile: null });
        return;
      }
      const user = { ...data.user, kind: '1', canManageSharedContent: false };
      const academy = data.academy;
      const chatAcademy = academy.certCode || academy.acaCode;
      dispatch(setUserInfo(user));
      dispatch(setUserInfo2({
        acaCode: chatAcademy, stCode: user.stCode, id: normalizeChatUserId(user.id, chatAcademy), id2: user.id,
        name: user.name || '', nickname: user.nickname || user.name || '', email: user.email || '',
        hp: user.hp || '', role: 'student', kind: '1', uid: '',
      }));
      dispatch(setIsLoggedIn(true));
      dispatch(setIsAdmin(false));
      dispatch(setBoardAdmin(false));
      dispatch(setAcaName(academy.acaName || 'AI K-EDU'));
      dispatch(setChoJoongGo(academy.choJoongGo || '전체'));
      dispatch(setIsPaid(academy.isPaid || 'N'));
      const persistent = isPersistent();
      const context = {
        acaCode: academy.acaCode, acaDbName: academy.acaDbName, s3_acaCode: academy.s3_acaCode || '',
        acaName: academy.acaName || 'AI K-EDU', choJoongGo: academy.choJoongGo || '전체',
        isPaid: academy.isPaid || 'N', certCode: academy.certCode || '',
        id: user.id, userName: user.name || user.nickname || '', stCode: user.stCode,
        userRole: 'student', email: user.email || '', hp: user.hp || '',
      };
      Object.entries(context).forEach(([key, value]) => fnc.setCookie(key, value, 30, persistent));
      setState({ status: 'ready', profile: { user, academy } });
    }).catch(error => {
      if (!active || controller.signal.aborted) return;
      if (readToken() !== token || tokenRef.current !== token) { syncSession(); return; }
      if (error.response?.status === 401) { invalidateSession(token); return; }
      setState({ status: error.response?.status === 403 ? 'forbidden' : 'error', profile: null });
    });
    return () => {
      active = false;
      controller.abort();
      if (requestRef.current === controller) requestRef.current = null;
    };
  }, [token, attempt, dispatch, syncSession, invalidateSession]);
  return { ...state, retry };
}
